// Operator Report: periods, call types, directions, demo data and helpers

export const PERIODS = [
  { value: 'today', labelKey: 'periodToday' },
  { value: 'week', labelKey: 'periodWeek' },
  { value: 'month', labelKey: 'periodMonth' },
  { value: 'custom', labelKey: 'periodCustom' },
]

export const CALL_TYPE_API_KEYS = {
  ANSWERED: 'answered',
  'ANSWER LATER': 'answered_later',
  'NO ANSWER': 'not_answered',
  BUSY: 'busy',
  FAILED: 'failed',
  CANCEL: 'canceled',
  CANCELED: 'canceled',
  IVR: 'ivr',
  'MUSIC HOLD': 'music_hold',
  MUSICHOLD: 'music_hold',
  VOICEMAIL: 'voicemail',
}

export const CALL_TYPES = [
  { key: 'answered', apiValues: ['ANSWERED'], color: '#22c55e', labelKey: 'callAnswered' },
  { key: 'answered_later', apiValues: ['ANSWER LATER'], color: '#6ee7b7', labelKey: 'callAnsweredLater' },
  { key: 'not_answered', apiValues: ['NO ANSWER'], color: '#ef4444', labelKey: 'callNotAnswered' },
  { key: 'busy', apiValues: ['BUSY'], color: '#f59e0b', labelKey: 'callBusy' },
  { key: 'failed', apiValues: ['FAILED'], color: '#dc2626', labelKey: 'callFailed' },
  { key: 'canceled', apiValues: ['CANCEL', 'CANCELED'], color: '#6b7280', labelKey: 'callCanceled' },
  { key: 'ivr', apiValues: ['IVR'], color: '#a855f7', labelKey: 'callIvr' },
  { key: 'music_hold', apiValues: ['MUSIC HOLD', 'MUSICHOLD'], color: '#eab308', labelKey: 'callMusicHold' },
  { key: 'voicemail', apiValues: ['VOICEMAIL'], color: '#14b8a6', labelKey: 'callVoicemail' },
]

// Call directions (status/direction from API may vary)
export const CALL_DIRECTIONS = [
  { key: 'incoming', color: '#3b82f6', labelKey: 'dirIncoming' },
  { key: 'outgoing', color: '#8b5cf6', labelKey: 'dirOutgoing' },
  { key: 'local', color: '#06b6d4', labelKey: 'dirLocal' },
  { key: 'transferred', color: '#ec4899', labelKey: 'dirTransferred' },
  { key: 'auto_calls', color: '#84cc16', labelKey: 'dirAutoCalls' },
]

export const DEMO_PREFIXES = ['37491', '37493', '37494', '37495', '37496', '37497', '37498', '37499', '37443', '37455']
export const DEMO_DISPOSITIONS = ['ANSWERED', 'ANSWERED', 'ANSWER LATER', 'NO ANSWER', 'BUSY', 'FAILED', 'CANCEL', 'IVR', 'MUSIC HOLD', 'VOICEMAIL']
export const DEMO_DIRECTIONS = ['incoming', 'incoming', 'outgoing', 'local', 'transferred', 'auto_calls']

export const VIEW_OVERVIEW = null
export const VIEW_CALL_COUNT = 'call-count'
export const VIEW_DIRECTIONS = 'directions'
export const VIEW_RATING = 'rating'
export const VIEW_NUMBERS = 'numbers'

export function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

export function normalizeDisposition(disp) {
  if (!disp) return 'not_answered'
  const u = String(disp).toUpperCase().trim()
  if (CALL_TYPE_API_KEYS[u] !== undefined) return CALL_TYPE_API_KEYS[u]
  if (u.includes('ANSWER LATER')) return 'answered_later'
  if (u.includes('NO ANSWER')) return 'not_answered'
  if (u.includes('CANCEL')) return 'canceled'
  if (u.includes('IVR')) return 'ivr'
  if (u.includes('MUSIC') && u.includes('HOLD')) return 'music_hold'
  if (u.includes('VOICEMAIL')) return 'voicemail'
  return 'not_answered'
}

export function normalizeDirection(row) {
  const s = (row?.status || row?.direction || '').toString().toLowerCase()
  if (s.includes('incoming') || s === 'in') return 'incoming'
  if (s.includes('outgoing') || s.includes('out call') || s === 'out') return 'outgoing'
  if (s.includes('local')) return 'local'
  if (s.includes('transfer')) return 'transferred'
  if (s.includes('auto')) return 'auto_calls'
  return row?.direction || 'incoming'
}

export function getRandomDemoCalls(count = 35) {
  const calls = []
  const now = Date.now()
  const dayMs = 24 * 60 * 60 * 1000
  for (let i = 0; i < count; i++) {
    const prefix = DEMO_PREFIXES[getRandomInt(0, DEMO_PREFIXES.length - 1)]
    const num = String(getRandomInt(100000, 999999))
    const disposition = DEMO_DISPOSITIONS[getRandomInt(0, DEMO_DISPOSITIONS.length - 1)]
    const direction = DEMO_DIRECTIONS[getRandomInt(0, DEMO_DIRECTIONS.length - 1)]
    const duration = (disposition === 'ANSWERED' || disposition === 'ANSWER LATER') ? getRandomInt(15, 600) : getRandomInt(0, 45)
    const startMs = now - getRandomInt(0, 7) * dayMs - getRandomInt(0, 86400) * 1000
    const rate = (disposition === 'ANSWERED' || disposition === 'ANSWER LATER') && Math.random() > 0.35 ? getRandomInt(1, 5) : null
    calls.push({
      uniqueid: `demo_${i}_${startMs}`,
      start: new Date(startMs).toISOString(),
      duration,
      disposition,
      direction,
      status: direction === 'incoming' ? 'Incoming Call' : direction === 'outgoing' ? 'Out Call' : direction,
      rate,
      extension: `3000${getRandomInt(100, 999)}`,
      destination: `${prefix}${num}`,
      in_num: `${prefix}${num}`,
      ext_num: `3000${getRandomInt(100, 999)}`,
    })
  }
  calls.sort((a, b) => new Date(b.start) - new Date(a.start))
  const byType = { answered: 0, answered_later: 0, not_answered: 0, busy: 0, failed: 0, canceled: 0 }
  calls.forEach((c) => {
    const key = normalizeDisposition(c.disposition)
    byType[key] = (byType[key] || 0) + 1
  })
  const totalDuration = calls.reduce((sum, c) => sum + (Number(c.duration) || 0), 0)
  return {
    received: calls.length,
    answered: byType.answered,
    answeredLater: byType.answered_later,
    notAnswered: byType.not_answered,
    busy: byType.busy,
    failed: byType.failed,
    canceled: byType.canceled,
    missed: byType.not_answered + byType.busy + byType.failed + byType.canceled,
    totalDuration,
    calls: calls.slice(0, 50),
  }
}
